import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Divider,
  TextField,
  Chip,
  Alert,
  InputAdornment,
  Typography,
  alpha,
  Fade,
  IconButton,
  Paper,
  CircularProgress,
  Button,
  LinearProgress,
} from "@mui/material";
import {
  QrCodeScanner,
  Close,
  Person,
  Phone,
  DirectionsBike,
  PedalBike,
  CheckCircle,
  Cancel,
  Warning,
  Info,
  AccessTime,
  Payment,
  Replay,
  ArrowForward,
} from "@mui/icons-material";

import MDButton from "shared/components/MDButton";

const BicicletaModalSalida = ({ open, onClose, onRegistrarSalida, onScanQr, loading = false }) => {
  const [qrInput, setQrInput] = useState("");
  const [scanning, setScanning] = useState(false);
  const [cycleData, setCycleData] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQrInput("");
      setCycleData(null);
      setError(null);
      setScanning(false);
      setSubmitting(false);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const handleScan = async (code) => {
    const cleanCode = (code || "").trim();
    if (!cleanCode) return;

    setError(null);
    setScanning(true);

    try {
      const result = await onScanQr(cleanCode);

      if (result?.success && result.data) {
        setCycleData(result.data);
      } else {
        setError(result?.error || "No se encontró información para este QR");
        setCycleData(null);
      }
    } catch (err) {
      setError(err.message || "Error al escanear QR");
      setCycleData(null);
    } finally {
      setScanning(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleScan(qrInput);
    }
  };

  const handleReset = () => {
    setQrInput("");
    setCycleData(null);
    setError(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleConfirmSalida = async () => {
    if (!cycleData) return;
    if (!cycleData.paid) {
      setError("No se puede registrar la salida: el pago está pendiente");
      return;
    }

    setSubmitting(true);
    try {
      const result = await onRegistrarSalida(cycleData);
      if (result?.success) {
        onClose();
      } else {
        setError(result?.error || "Error al registrar la salida");
      }
    } catch (err) {
      setError(err.message || "Error al registrar la salida");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleString("es-CO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calcularTiempoTranscurrido = (enterAt) => {
    if (!enterAt) return "N/A";
    const inicio = new Date(enterAt);
    const ahora = new Date();
    const diffMs = ahora - inicio;
    if (diffMs < 0) return "0 min";
    const horas = Math.floor(diffMs / (1000 * 60 * 60));
    const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (horas > 0) return `${horas}h ${minutos}min`;
    return `${minutos} min`;
  };

  const canConfirm = cycleData && cycleData.paid && !submitting && !loading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Fade}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "16px", maxHeight: "92vh" },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pt: 2.5,
          px: 3,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "#fff",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                bgcolor: alpha("#c62828", 0.1),
                borderRadius: "10px",
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <QrCodeScanner sx={{ fontSize: "1.5rem", color: "#c62828" }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Registrar Salida
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Escanee el código QR del ticket para registrar la salida
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "#64748b" }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: 3, pb: 2, overflowY: "auto", bgcolor: "#f8fafc" }}>
        {/* ============ SCANNER INPUT ============ */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: "12px",
            border: "2px dashed",
            borderColor: cycleData ? "#4caf50" : scanning ? "#1976d2" : "#cbd5e1",
            bgcolor: cycleData ? alpha("#4caf50", 0.03) : "#fff",
            transition: "all 0.3s ease",
          }}
        >
          <Box textAlign="center" mb={2}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: cycleData
                  ? alpha("#4caf50", 0.15)
                  : scanning
                  ? alpha("#1976d2", 0.15)
                  : alpha("#64748b", 0.1),
                mb: 1,
              }}
            >
              {scanning ? (
                <CircularProgress size={32} sx={{ color: "#1976d2" }} />
              ) : cycleData ? (
                <CheckCircle sx={{ fontSize: 32, color: "#4caf50" }} />
              ) : (
                <QrCodeScanner sx={{ fontSize: 32, color: "#64748b" }} />
              )}
            </Box>
            <Typography variant="subtitle1" fontWeight="700" gutterBottom>
              {scanning
                ? "Buscando información..."
                : cycleData
                ? "✅ QR Escaneado"
                : "Escanee el código QR"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {cycleData
                ? "Verifique los datos abajo"
                : "Acerque el lector al QR o escriba el código manualmente"}
            </Typography>
          </Box>

          <TextField
            inputRef={inputRef}
            fullWidth
            autoFocus
            value={qrInput}
            onChange={(e) => setQrInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Esperando lectura del QR..."
            disabled={scanning || !!cycleData}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <QrCodeScanner sx={{ color: "#c62828" }} />
                </InputAdornment>
              ),
              endAdornment: cycleData && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleReset} title="Escanear otro">
                    <Replay sx={{ fontSize: 20 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "56px",
                fontSize: "1.1rem",
                fontFamily: "monospace",
                bgcolor: "#fafafa",
              },
            }}
          />

          <Box display="flex" justifyContent="center" gap={1} mt={2}>
            <MDButton
              variant="contained"
              size="small"
              startIcon={<ArrowForward />}
              onClick={() => handleScan(qrInput)}
              disabled={!qrInput.trim() || scanning || !!cycleData}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                bgcolor: "#c62828",
                color: "#fff !important",
                "&:hover": { bgcolor: "#b71c1c" },
              }}
            >
              Buscar
            </MDButton>
            {cycleData && (
              <MDButton
                variant="outlined"
                size="small"
                startIcon={<Replay />}
                onClick={handleReset}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  color: "#1a237e",
                  borderColor: "#1a237e",
                }}
              >
                Escanear otro
              </MDButton>
            )}
          </Box>
        </Paper>

        {/* ============ ERROR ============ */}
        {error && (
          <Alert
            severity="error"
            icon={<Warning />}
            onClose={() => setError(null)}
            sx={{ mb: 3, borderRadius: "10px" }}
          >
            {error}
          </Alert>
        )}

        {/* ============ RESUMEN DEL CICLO ============ */}
        {cycleData && (
          <Fade in>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: alpha("#1976d2", 0.3),
                bgcolor: "#fff",
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Info sx={{ color: "#1976d2" }} />
                <Typography variant="subtitle1" fontWeight="700">
                  Información del Registro
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* USUARIO */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "10px",
                      bgcolor: alpha("#1976d2", 0.05),
                      border: "1px solid",
                      borderColor: alpha("#1976d2", 0.15),
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Person sx={{ color: "#1976d2", fontSize: 20 }} />
                      <Typography
                        variant="caption"
                        fontWeight="700"
                        sx={{ color: "#1976d2", letterSpacing: 0.5 }}
                      >
                        USUARIO
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="700" sx={{ color: "#1e293b" }}>
                      {cycleData.user?.names} {cycleData.user?.lastnames}
                    </Typography>
                    <Box display="flex" gap={3} mt={0.5} flexWrap="wrap">
                      <Typography variant="caption" color="text.secondary">
                        <strong>Documento:</strong> {cycleData.user?.identification}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Celular:</strong> {cycleData.user?.phone || "—"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* BICICLETA */}
                <Grid item xs={12} md={7}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "10px",
                      bgcolor: alpha("#5c6bc0", 0.05),
                      border: "1px solid",
                      borderColor: alpha("#5c6bc0", 0.15),
                      height: "100%",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <DirectionsBike sx={{ color: "#5c6bc0", fontSize: 20 }} />
                      <Typography
                        variant="caption"
                        fontWeight="700"
                        sx={{ color: "#5c6bc0", letterSpacing: 0.5 }}
                      >
                        BICICLETA
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="700" sx={{ color: "#1e293b" }}>
                      {cycleData.bike?.brand || "N/A"} — {cycleData.bike?.color || "N/A"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Serie:</strong> {cycleData.bike?.code || "—"}
                    </Typography>
                  </Box>
                </Grid>

                {/* GANCHO */}
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "10px",
                      bgcolor: alpha("#ff9800", 0.05),
                      border: "1px solid",
                      borderColor: alpha("#ff9800", 0.15),
                      height: "100%",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <PedalBike sx={{ color: "#ff9800", fontSize: 20 }} />
                      <Typography
                        variant="caption"
                        fontWeight="700"
                        sx={{ color: "#ff9800", letterSpacing: 0.5 }}
                      >
                        GANCHO
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="700" sx={{ color: "#1e293b" }}>
                      #{String(cycleData.harness || "").padStart(2, "0")}
                    </Typography>
                  </Box>
                </Grid>

                {/* TIEMPOS */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "10px",
                      bgcolor: "#fafafa",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <AccessTime sx={{ color: "#64748b", fontSize: 20 }} />
                      <Typography
                        variant="caption"
                        fontWeight="700"
                        sx={{ color: "#64748b", letterSpacing: 0.5 }}
                      >
                        INGRESO
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="600">
                      {formatDateTime(cycleData.enter_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Hace {calcularTiempoTranscurrido(cycleData.enter_at)}
                    </Typography>
                  </Box>
                </Grid>

                {/* ESTADO DE PAGO */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "10px",
                      bgcolor: cycleData.paid ? alpha("#4caf50", 0.08) : alpha("#c62828", 0.08),
                      border: "2px solid",
                      borderColor: cycleData.paid ? "#4caf50" : "#c62828",
                      height: "100%",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Payment
                        sx={{
                          color: cycleData.paid ? "#4caf50" : "#c62828",
                          fontSize: 20,
                        }}
                      />
                      <Typography
                        variant="caption"
                        fontWeight="700"
                        sx={{
                          color: cycleData.paid ? "#4caf50" : "#c62828",
                          letterSpacing: 0.5,
                        }}
                      >
                        ESTADO DE PAGO
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      {cycleData.paid ? (
                        <>
                          <CheckCircle sx={{ color: "#4caf50", fontSize: 28 }} />
                          <Typography variant="h6" fontWeight="700" sx={{ color: "#2e7d32" }}>
                            PAGADO
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Cancel sx={{ color: "#c62828", fontSize: 28 }} />
                          <Typography variant="h6" fontWeight="700" sx={{ color: "#c62828" }}>
                            PENDIENTE
                          </Typography>
                        </>
                      )}
                    </Box>
                    {!cycleData.paid && (
                      <Typography
                        variant="caption"
                        sx={{ color: "#c62828", display: "block", mt: 1 }}
                      >
                        ⚠️ Debe completar el pago antes de retirar la bicicleta
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Fade>
        )}

        {submitting && <LinearProgress sx={{ mt: 2, borderRadius: 2 }} />}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "#fff",
        }}
      >
        <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
          <MDButton
            onClick={onClose}
            variant="outlined"
            startIcon={<Close />}
            sx={{
              px: 3,
              minWidth: 120,
              borderRadius: "8px",
              height: "44px",
              color: "#1a237e",
              borderColor: "#1a237e",
            }}
          >
            Cancelar
          </MDButton>

          <MDButton
            onClick={handleConfirmSalida}
            variant="contained"
            startIcon={
              submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CheckCircle />
            }
            disabled={!canConfirm}
            sx={{
              px: 4,
              minWidth: 220,
              borderRadius: "8px",
              fontWeight: "bold",
              height: "44px",
              color: "#ffffff !important",
              bgcolor: "#c62828",
              "&:hover": { bgcolor: "#b71c1c" },
              "&.Mui-disabled": {
                bgcolor: alpha("#c62828", 0.3),
                color: "#ffffff !important",
              },
            }}
          >
            {submitting
              ? "PROCESANDO..."
              : !cycleData
              ? "ESCANEE UN QR"
              : !cycleData.paid
              ? "PAGO PENDIENTE"
              : "REGISTRAR SALIDA"}
          </MDButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

BicicletaModalSalida.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRegistrarSalida: PropTypes.func.isRequired,
  onScanQr: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

BicicletaModalSalida.defaultProps = {
  loading: false,
};

export default BicicletaModalSalida;
